import { useForm, useFormState } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { SwitchField } from "src/ui/shared/form/SwitchField";

import { Row, SectionDescription } from "@dashboard/shared/styles";
import { Box, SwitchProps, useTheme } from "@mui/material";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { Tooltip } from "@shared/Tooltip";

import {
    CollapsibleProvider, CollapsibleSectionDetails, CollapsibleSectionSummary,
    CollapsibleSectionTitle, useCollapsiblePersistence,
} from "../../shared/components/CollapsibleSection";
import { Provisioning } from "../types";

const options = [
    { label: i18n.t("pages:provisioningPage.input.automaticSetup.options.manual"), value: 0 },
    { label: i18n.t("pages:provisioningPage.input.automaticSetup.options.automatic"), value: 1 },
];

export const SetupAutomation = () => {
    const { value, onChange } = useCollapsiblePersistence("provisioningPage.collapsedSections", "SetupAutomation");

    const { t } = useTranslation(["pages"]);
    const parentForm = useForm();
    const theme = useTheme();
    const { values: parentFormValues } = useFormState<Provisioning>();

    const onSetupChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "number") {
            return;
        }
        parentForm.change("settings.automaticSetup", !!selectedOption.value);
    };

    const onEulaChange: SwitchProps["onChange"] = (_e) => {
        parentForm.change("settings.automaticSetup", false);
    };

    return (
        <CollapsibleProvider initialValue={value} onChange={onChange}>
            <CollapsibleSectionSummary>
                <CollapsibleSectionTitle>{t("pages:provisioningPage.section.setupAutomation.title")}</CollapsibleSectionTitle>
            </CollapsibleSectionSummary>
            <CollapsibleSectionDetails>
                <SectionDescription>{t("pages:provisioningPage.section.setupAutomation.description")}</SectionDescription>
                <Box style={{ paddingLeft: theme.spacing(1), marginTop: theme.spacing(1), marginBottom: theme.spacing(5) }}>
                    <SwitchField
                        name="settings.acceptedEula"
                        onChange={onEulaChange}
                        labelPlacement="end"
                        label={
                            <Trans
                                ns="pages"
                                i18nKey="provisioningPage.input.eulaAgreement.caption"
                                components={{
                                    CustomLink: (
                                        <a href="https://emteria.com/legal/terms-of-use" target="_blank" rel="noreferrer" />
                                    ),
                                }}
                            />
                        }
                    />
                </Box>
                <Tooltip
                    childrenBoxStyles={{ display: "block" }}
                    hide={parentFormValues?.settings?.acceptedEula}
                    title={t("pages:provisioningPage.tooltip.automaticSetup")}
                >
                    <Row>
                        <SelectField
                            label={t("pages:provisioningPage.input.automaticSetup.caption")}
                            options={options}
                            onChange={onSetupChange}
                            value={options.find(
                                (option) => option.value === (parentFormValues?.settings?.automaticSetup ? 1 : 0),
                            )}
                            disabled={!parentFormValues?.settings?.acceptedEula}
                            fullWidth
                        />
                    </Row>
                </Tooltip>
            </CollapsibleSectionDetails>
        </CollapsibleProvider>
    );
};
